import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";
import { useDealerLog, LOG_ACTIONS } from "@/hooks/useDealerLog";

interface CategoryRow {
  id: string;
  name: string;
}

interface ProductRow {
  id: string;
  name: string;
  category_id: string | null;
  sale_price: number;
  purchase_price: number;
  stock: number;
  barcode: string | null;
}

interface RestaurantQuickStockModalProps {
  open: boolean;
  onClose: () => void;
}

export default function RestaurantQuickStockModal({ open, onClose }: RestaurantQuickStockModalProps) {
  const { user } = useAuth();
  const { logAction } = useDealerLog();
  const dealerId = user?.dealerId;

  const [tab, setTab] = useState<"category" | "product">("category");
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [products, setProducts] = useState<ProductRow[]>([]);

  const [newCategoryName, setNewCategoryName] = useState("");
  const [savingCategory, setSavingCategory] = useState(false);
  const [categoryError, setCategoryError] = useState("");

  const [newProduct, setNewProduct] = useState({
    name: "",
    barcode: "",
    categoryId: "",
    salePrice: "",
    purchasePrice: "",
    stock: "",
  });
  const [savingProduct, setSavingProduct] = useState(false);
  const [productError, setProductError] = useState("");

  const loadData = useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    try {
      const supabase = getSupabase();
      const [catRes, prodRes] = await Promise.all([
        supabase.from("categories").select("id, name").eq("dealer_id", dealerId).order("name"),
        supabase
          .from("products")
          .select("id, name, category_id, sale_price, purchase_price, stock, barcode, product_code")
          .eq("dealer_id", dealerId)
          .order("name"),
      ]);
      setCategories((catRes.data as CategoryRow[]) || []);
      setProducts(
        ((prodRes.data as Array<Record<string, unknown>>) || [])
          .filter((p) => p.product_code !== "REST-001")
          .map((p) => ({
            id: p.id as string,
            name: p.name as string,
            category_id: (p.category_id as string) || null,
            sale_price: Number(p.sale_price) || 0,
            purchase_price: Number(p.purchase_price) || 0,
            stock: Number(p.stock) || 0,
            barcode: (p.barcode as string) || null,
          }))
      );
    } catch {
      setCategories([]);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    if (open) {
      setTab("category");
      setNewCategoryName("");
      setCategoryError("");
      setNewProduct({ name: "", barcode: "", categoryId: "", salePrice: "", purchasePrice: "", stock: "" });
      setProductError("");
      loadData();
    }
  }, [open, loadData]);

  if (!open) return null;

  const categoryName = (id: string | null) => categories.find((c) => c.id === id)?.name || "Kategorisiz";

  const addCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) {
      setCategoryError("Lütfen kategori adı girin!");
      return;
    }
    if (categories.some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      setCategoryError("Bu isimde bir kategori zaten var!");
      return;
    }
    if (!dealerId) {
      setCategoryError("Bayi bilgisi bulunamadı.");
      return;
    }
    setSavingCategory(true);
    setCategoryError("");
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("categories")
        .insert({ name, dealer_id: dealerId })
        .select("id, name")
        .single();
      if (error) throw error;

      setCategories((prev) => [...prev, { id: data.id as string, name: data.name as string }].sort((a, b) => a.name.localeCompare(b.name, "tr")));
      setNewCategoryName("");
      logAction(dealerId, user?.id || null, LOG_ACTIONS.CATEGORY_ADD, `Kategori eklendi: ${name}`);
    } catch (e: any) {
      setCategoryError(e.message || "Kategori eklenirken hata oluştu.");
    } finally {
      setSavingCategory(false);
    }
  };

  const addProduct = async () => {
    const name = newProduct.name.trim();
    const salePrice = parseFloat(newProduct.salePrice);
    if (!name) {
      setProductError("Lütfen ürün adı girin!");
      return;
    }
    if (!newProduct.salePrice || isNaN(salePrice) || salePrice <= 0) {
      setProductError("Lütfen geçerli bir satış fiyatı girin!");
      return;
    }
    if (!dealerId) {
      setProductError("Bayi bilgisi bulunamadı.");
      return;
    }
    setSavingProduct(true);
    setProductError("");
    try {
      const supabase = getSupabase();
      const purchasePrice = newProduct.purchasePrice ? parseFloat(newProduct.purchasePrice) : 0;
      const stock = newProduct.stock ? parseInt(newProduct.stock, 10) : 0;

      const { data, error } = await supabase
        .from("products")
        .insert({
          name,
          barcode: newProduct.barcode.trim() || null,
          category_id: newProduct.categoryId || null,
          sale_price: salePrice,
          purchase_price: purchasePrice,
          stock,
          dealer_id: dealerId,
        })
        .select("id, name, category_id, sale_price, purchase_price, stock, barcode")
        .single();
      if (error) throw error;

      const p = data as Record<string, unknown>;
      setProducts((prev) => [
        ...prev,
        {
          id: p.id as string,
          name: p.name as string,
          category_id: (p.category_id as string) || null,
          sale_price: Number(p.sale_price) || 0,
          purchase_price: Number(p.purchase_price) || 0,
          stock: Number(p.stock) || 0,
          barcode: (p.barcode as string) || null,
        },
      ].sort((a, b) => a.name.localeCompare(b.name, "tr")));
      setNewProduct({ name: "", barcode: "", categoryId: newProduct.categoryId, salePrice: "", purchasePrice: "", stock: "" });
      logAction(dealerId, user?.id || null, LOG_ACTIONS.PRODUCT_ADD, `Ürün eklendi: ${name} (₺${salePrice.toFixed(2)})`);
    } catch (e: any) {
      setProductError(e.message || "Ürün eklenirken hata oluştu.");
    } finally {
      setSavingProduct(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end lg:items-center justify-center bg-black/30" onClick={onClose}>
      <div
        className="bg-background-50 rounded-t-2xl lg:rounded-2xl w-full max-w-lg mx-0 lg:mx-4 max-h-[88vh] flex flex-col overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center">
              <i className="ri-archive-line text-primary-600"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-950">Stok Hızlı Ekle</p>
              <p className="text-xs text-foreground-400">Restoran POS için kategori ve ürün ekleyin</p>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1.5 mx-4 mt-3 bg-background-100 rounded-xl shrink-0">
          <button
            onClick={() => setTab("category")}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
              tab === "category" ? "bg-background-50 text-foreground-950 shadow-sm" : "text-foreground-400 hover:text-foreground-600"
            }`}
          >
            <i className="ri-price-tag-3-line"></i> Kategori
          </button>
          <button
            onClick={() => setTab("product")}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
              tab === "product" ? "bg-background-50 text-foreground-950 shadow-sm" : "text-foreground-400 hover:text-foreground-600"
            }`}
          >
            <i className="ri-restaurant-2-line"></i> Ürün
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {tab === "category" ? (
            <>
              <div className="bg-background-100 rounded-xl p-3 space-y-2">
                <label className="block text-xs font-medium text-foreground-500">Yeni Kategori Adı</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => { setNewCategoryName(e.target.value); setCategoryError(""); }}
                    onKeyDown={(e) => { if (e.key === "Enter") addCategory(); }}
                    placeholder="Örn: Tatlılar"
                    className="flex-1 px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                  <button
                    onClick={addCategory}
                    disabled={savingCategory}
                    className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingCategory ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>}
                  </button>
                </div>
                {categoryError && (
                  <p className="text-xs text-accent-600 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i> {categoryError}
                  </p>
                )}
              </div>

              <div>
                <p className="text-xs font-medium text-foreground-400 uppercase tracking-wider mb-2">
                  Mevcut Kategoriler {categories.length > 0 && `(${categories.length})`}
                </p>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
                  </div>
                ) : categories.length === 0 ? (
                  <p className="text-xs text-foreground-400 text-center py-6">Henüz kategori eklenmemiş</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {categories.map((c) => (
                      <span key={c.id} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                        <i className="ri-price-tag-3-line"></i> {c.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="bg-background-100 rounded-xl p-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Ürün Adı *</label>
                  <input
                    type="text"
                    value={newProduct.name}
                    onChange={(e) => { setNewProduct({ ...newProduct, name: e.target.value }); setProductError(""); }}
                    placeholder="Örn: Cheeseburger"
                    className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Kategori</label>
                    <select
                      value={newProduct.categoryId}
                      onChange={(e) => setNewProduct({ ...newProduct, categoryId: e.target.value })}
                      className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
                    >
                      <option value="">Kategorisiz</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Barkod</label>
                    <input
                      type="text"
                      value={newProduct.barcode}
                      onChange={(e) => setNewProduct({ ...newProduct, barcode: e.target.value })}
                      placeholder="Opsiyonel"
                      className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Satış Fiyatı *</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={newProduct.salePrice}
                      onChange={(e) => { setNewProduct({ ...newProduct, salePrice: e.target.value }); setProductError(""); }}
                      placeholder="0.00"
                      className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Alış Fiyatı</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={newProduct.purchasePrice}
                      onChange={(e) => setNewProduct({ ...newProduct, purchasePrice: e.target.value })}
                      placeholder="0.00"
                      className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Stok</label>
                    <input
                      type="number"
                      inputMode="numeric"
                      value={newProduct.stock}
                      onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                      placeholder="0"
                      className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>
                </div>

                {productError && (
                  <p className="text-xs text-accent-600 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i> {productError}
                  </p>
                )}

                <button
                  onClick={addProduct}
                  disabled={savingProduct}
                  className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingProduct ? (
                    <span className="flex items-center justify-center gap-1.5">
                      <i className="ri-loader-4-line animate-spin"></i> Kaydediliyor...
                    </span>
                  ) : (
                    <>
                      <i className="ri-add-line mr-1"></i> Ürünü Kaydet
                    </>
                  )}
                </button>
              </div>

              <div>
                <p className="text-xs font-medium text-foreground-400 uppercase tracking-wider mb-2">
                  Mevcut Ürünler {products.length > 0 && `(${products.length})`}
                </p>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
                  </div>
                ) : products.length === 0 ? (
                  <p className="text-xs text-foreground-400 text-center py-6">Henüz ürün eklenmemiş</p>
                ) : (
                  <div className="divide-y divide-background-200/70 border border-background-200/70 rounded-xl overflow-hidden">
                    {products.map((p) => (
                      <div key={p.id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <div className="min-w-0">
                          <p className="font-medium text-foreground-900 truncate">{p.name}</p>
                          <p className="text-xs text-foreground-400">{categoryName(p.category_id)} · Stok: {p.stock}</p>
                        </div>
                        <span className="font-semibold text-foreground-950 shrink-0 ml-2">₺{p.sale_price.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
